import React, { useCallback, useState, useMemo } from 'react';
import _ from 'lodash';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import SimpleTable, { SortDirection, createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';
import useAlerts from 'utils/hooks/useAlerts';
import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';
import useAPI from 'services/useAPI';
import { useTranslation } from 'localization/useTranslation';
import { useHistory } from 'react-router-dom';
import NewOrganizationDialog from './NewOrganizationDialog';
import useBoolean from 'utils/hooks/useBoolean';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';
import { orgKeys } from './queries';


import T from '../../../localization/LocalizationKeys';
// TODO translate
const noParent = { id: null, displayName: 'No Parent' };


const columns = [
  {
    label: T.common.id,
    path: 'id',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.common.parent,
    path: 'parent.displayName',
    isSortable: true
  }
];

const DefaultSortBy = {
  path: 'displayName',
  direction: SortDirection.DESC
};

const AdminOrganizationPage = ({ breadcrumbs }) => {
  const api = useAPI();
  const queryClient = useQueryClient();
  const history = useHistory();
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const [newOrgDialogOpen, showNewOrgDialog, hideNewOrgDialog] = useBoolean(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const T = useTranslation();


  const organizationsQuery = useQuery(orgKeys.organizations(),
    createQueryRequest(api.core.getOrganizations),
    {
      onError: useCallback((error) => {
        addErrorAlert(error.message);
      }, [addErrorAlert])
    }
  );
  const organizations = organizationsQuery.data ?? [];
  const isLoadingOrganizations = organizationsQuery.isLoading;

  const createOrganization = useMutation({
    mutationFn: createMutationRequest(api.core.createOrganization),
    onSuccess: useCallback((org) => {
      addSuccessAlert(T.common.request.create.success);
      history.push(`/admin/organizations/${org.id}`);
    }, [T.common.request.create.success, addSuccessAlert, history]),
    onError: useCallback((error) => {
      addErrorAlert(error.message);
    }, [addErrorAlert])
  });

  const orgTemplates = useCallback(() => {
    history.push('/admin/organizations/templates');
  }, [history]);

  const onRowClick = useCallback((row) => {
    history.push(`/admin/organizations/${row.id}`);
  }, [history]);

  const onSave = useCallback((data) => {
    hideNewOrgDialog();
    const org = _.pick(data, ['displayName', 'description', 'parentOrganizationId', 'privileges']);
    createOrganization.mutate(org);
  }, [hideNewOrgDialog, createOrganization]);


  const onDelete = useCallback((org) => setConfirmDeleteItem(org), []);
  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);

  const deleteOrganization = useMutation({
    mutationFn: createMutationRequest(api.core.deleteOrganization),
    onMutate: (deleteItem) => {
      // Optimistic delete
      const oldData = [...queryClient.getQueryData(orgKeys.organizations())];
      queryClient.setQueryData(orgKeys.organizations(), old => {
        return _.reject(old, { id: deleteItem.id });
      });
      return { deleteItem, oldData };
    },
    onSuccess: () => {
      addSuccessAlert(T.common.request.delete.success);
      setConfirmDeleteItem(null);
      // Refresh list
      organizationsQuery.refetch({ throwOnError: true });
    },
    onError: (unusedError, unusedVariables, context) => {
      addErrorAlert(T.common.request.delete.failed);
      // Readd the old data
      queryClient.setQueryData(orgKeys.organizations(), context?.oldData);
    }
  });

  return (
    <ContentPage breadcrumb={breadcrumbs} help="admin/organizations/list">
      { alerts }
      <SimpleTable
        isLoading={isLoadingOrganizations}
        data={organizations}
        columns={columns}
        sortBy={DefaultSortBy}
        globalActions={[{ onClick: showNewOrgDialog, isLoading: createOrganization.isLoading }, { onClick: orgTemplates, label: 'Templates', variant: 'secondary' }]}
        onRowClick={onRowClick}
        actions={actions}
        compact
        usePaging
      />
      <NewOrganizationDialog
        isOpen={newOrgDialogOpen}
        onSave={onSave}
        onCancel={hideNewOrgDialog}
        parentOrgs={[noParent, ...organizations]}
      />
      <ConfirmDeleteModal
        isLoading={deleteOrganization.isLoading}
        onConfirm={item => deleteOrganization.mutate(item.id)}
        item={confirmDeleteItem}
        itemName={confirmDeleteItem?.displayName ?? T.common.noname}
        onCancel={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminOrganizationPage;
